/**
 * Conversation Page Facade (100% Shared)
 * 
 * Composes all necessary facades for the conversation page.
 * Uses dependency injection pattern to remain platform-agnostic.
 * 
 * This facade is 100% shared between web and plugin.
 * Platform-specific implementations (navigation, auth) are injected.
 * 
 * Usage (Web):
 * ```tsx
 * const navigation = useWebNavigation();
 * const auth = useAuthDialog();
 * const facade = useConversationPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * Usage (Plugin):
 * ```tsx
 * const navigation = usePluginNavigation();
 * const auth = usePairingCodeAuth();
 * const facade = useConversationPageFacade({ navigation, auth, showToast });
 * ```
 * 
 * @module useConversationPageFacade
 * @category Facades/Pages
 */

import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { selectIsAppLoading, selectCurrentProjectId, selectIsLoggedIn, setCurrentProjectId, getUserProfile } from '../../../store/slices/appSlice';
import { selectConversationState, selectConversationMessages, selectConversationMode, selectConversationName, selectIsOnboardingPrototyper, updateBanner, setName, setMode, resetConversation } from '../../../store/slices/conversationSlice';
import { updateProjectMode } from '../../../store/slices/projectSlice';
import { useHeaderConfig } from '../../useHeaderConfig';
import { useConversationFacade } from '../shared/useConversationFacade';
import { useConversationStreamingFacade } from '../shared/useConversationStreamingFacade';
import { useDrawingFacade } from '../shared/useDrawingFacade';
import { usePrototyperFacade } from '../shared/usePrototyperFacade';
import { useConversationHeadersFacade } from '../shared/useConversationHeadersFacade';
import { useProjectFacade } from '../shared/useProjectFacade';
import { useSidebarConfig } from '../shared/useSidebarConfig';
import { useDesignInsightsFacade } from '../shared/useDesignInsightsFacade';
import type { NavigationFacade, AuthFacade, ShowToastFn } from '../shared/types';
import { getBestConversationId, isValidConversationId } from '../../../utils/conversationUtils';
import type { ConversationHeaders, QuestionnaireType, Message } from '../../../types/conversation.types';
import type { ProjectMode } from '../../../types/project.types';
import { handleFigJamPluginAction, cleanUserActions } from '@ideoz/core/api/services';

// ==================== Types ====================

export interface ConversationPageFacadeParams {
  /**
   * Platform-specific navigation implementation
   */
  navigation: NavigationFacade;

  /**
   * Platform-specific auth implementation
   */
  auth: AuthFacade;

  /**
   * Platform-specific toast notification function
   */
  showToast: ShowToastFn;

  /**
   * Platform identifier ('web' or 'plugin')
   */
  platform?: 'web' | 'plugin';

  /**
   * URL parameter for conversation ID (from routing)
   */
  urlConversationId?: string | null;

  /**
   * Callback when project creation is ready
   */
  onProjectCreationReady?: (conversationId: string) => void;

  /**
   * Callback when account creation is required
   */
  onAccountCreationRequired?: () => void;

  /**
   * Optional initial mode (e.g., from URL parameter for plugin redirects)
   */
  initialMode?: 'uncover' | 'prototyper';

  /**
   * Callback to refresh user profile (to update credit balance after prototype creation)
   */
  onRefreshUserProfile?: () => Promise<void>;
}

export interface ConversationPageFacade {
  // ========== State ==========
  state: {
    isLoggedIn: boolean;
    isAppLoading: boolean;
    currentProjectId: string | null;
    currentConversationId: string | null;
    messages: any[];
    isStreaming: boolean;
    isLoading: boolean;
    isLoadingDetails: boolean;
    isActive: boolean;
    session_id: string | null;
    conversationId: string | null;
    name: string;
    init_message: string;
    skip_analysis: boolean;
    initialAttachmentIds?: string[];
    llmFamily?: string;
    planDetails?: any;
    isOnboardingPrototyper?: boolean;
    initiatedFromUncover?: boolean;
  };

  // ========== Sidebar ==========
  sidebar: ReturnType<typeof useSidebarConfig>;

  // ========== Mode ==========
  mode: {
    currentMode: ProjectMode;
    handleModeChange: (mode: ProjectMode) => void;
  };

  // ========== Header ==========
  header: ReturnType<typeof useHeaderConfig>;

  // ========== Navigation ==========
  navigation: NavigationFacade;

  // ========== Conversation Management ==========
  conversation: ReturnType<typeof useConversationFacade>;

  // ========== Streaming ==========
  streaming: ReturnType<typeof useConversationStreamingFacade>;

  // ========== Drawing ==========
  drawing: ReturnType<typeof useDrawingFacade>;

  // ========== Prototyper ==========
  prototyper: ReturnType<typeof usePrototyperFacade>;

  // ========== Design Insights ==========
  designInsights: ReturnType<typeof useDesignInsightsFacade>;

  // ========== Headers ==========
  headers: ReturnType<typeof useConversationHeadersFacade>;

  // ========== Project ==========
  project: ReturnType<typeof useProjectFacade>;

  // ========== Auth ==========
  auth: AuthFacade;

  // ========== Actions ==========
  actions: {
    /**
     * Submit a message in the conversation
     * @param modeOverride - Optional mode override (e.g., 'prototyper' for generating prototype from uncover mode)
     */
    submitMessage: (message: string, attachmentIds?: string[], onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void, modeOverride?: 'uncover' | 'prototyper') => void;

    /**
     * Cancel ongoing streaming
     */
    cancelStreaming: () => void;

    /**
     * Load conversation history
     */
    loadHistory: (conversationId: string) => Promise<Message[]>;

    /**
     * Create a drawing (journey map, five whys, etc.)
     */
    createDrawing: (questionnaireType: string) => Promise<void>;

    /**
     * Create a prototype from conversation
     */
    createPrototype: () => Promise<void>;

    /**
     * Cancel drawing creation
     */
    cancelDrawing: () => void;

    /**
     * Navigate to conversation
     */
    navigateToConversation: (conversationId: string) => void;

    /**
     * Navigate to project details
     */
    navigateToProjectDetails: (projectId: string) => void;

    /**
     * Send feedback (like/dislike) for a message
     */
    sendFeedback: (type: 'like' | 'dislike', messageId: string, projectId?: string | null) => Promise<void>;

    /**
     * Handle FigJam plugin action - add user action for opening in FigJam
     */
    handleFigJamAction: (conversationId: string, conversationName: string, generateType: string | null, projectId?: string | null, hiddenMessage?: string | null) => Promise<void>;

    /**
     * Clean user actions from backend storage
     * @param actionType - The type of action to clean (e.g., 'openConversationAndDrawing')
     */
    cleanUserActions: (actionType: string) => Promise<void>;
  };
}

/**
 * Conversation Page Facade Hook
 * 
 * Composes all necessary facades and state for the conversation page.
 * 100% shared between web and plugin through dependency injection.
 */
export function useConversationPageFacade({
  navigation,
  auth,
  showToast,
  platform = 'web',
  urlConversationId,
  onProjectCreationReady,
  onAccountCreationRequired,
  initialMode,
  onRefreshUserProfile,
}: ConversationPageFacadeParams): ConversationPageFacade {
  // ========== Global State ==========
  const dispatch = useAppDispatch();
  const isAppLoading = useAppSelector(selectIsAppLoading);
  const isLoggedIn = useAppSelector(selectIsLoggedIn);
  const currentProjectId = useAppSelector(selectCurrentProjectId);
  const conversationState = useAppSelector(selectConversationState);
  const conversationName = useAppSelector(selectConversationName);
  const messages = useAppSelector(selectConversationMessages);
  const conversationMode = useAppSelector(selectConversationMode);
  const isOnboardingPrototyper = useAppSelector(selectIsOnboardingPrototyper);

  // ========== Plan Details from Redux ==========
  const planDetails = useAppSelector(state => state.app.planDetails);
  // Try to get accessToken from auth facade, fallback to empty string
  const accessToken = (auth && (auth as any).accessToken) ? (auth as any).accessToken : '';
  useEffect(() => {
    if (accessToken) {
      dispatch(getUserProfile(accessToken));
    }
  }, [accessToken, dispatch]);

  // ========== Local State ==========
  const [actualConversationId, setActualConversationId] = useState<string | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  // Map conversationMode to ProjectMode, with URL mode taking priority
  const [currentMode, setCurrentMode] = useState<ProjectMode>(() => {
    if (initialMode) return initialMode;
    if (conversationMode === 'prototyper') return 'prototyper';
    return 'uncover';
  });
  const hasNavigatedRef = useRef(false);
  const hasFetchedDetailsRef = useRef<Set<string>>(new Set());
  const lastLoadedConversationRef = useRef<string | null>(null);

  // ========== Computed Values ==========
  const currentConversationId = getBestConversationId(
    actualConversationId,
    urlConversationId,
    conversationState.conversationId
  );

  // ========== Reusable Facades ==========
  const conversation = useConversationFacade();
  const project = useProjectFacade();

  // ========== Streaming Facade ==========
  const streaming = useConversationStreamingFacade();

  // ========== Drawing Facade ==========
  const drawing = useDrawingFacade(
    currentProjectId || '',
    platform === 'plugin' ? {
      messages,
      conversationId: currentConversationId,
      onUpdateBanner: useCallback((messageId: string, bannerType: 'success' | 'error', drawType: string) => {
        dispatch(updateBanner({
          messageId,
          bannerType,
          drawType,
          bannerPosition: 'bottom'
        }));
      }, [dispatch]),
      onSendHiddenMessage: useCallback((message: string, conversationId: string, actionType: string) => {
        streaming.createStreamingConversation(
          message,
          conversationId,
          undefined, // onComplete callback
          undefined, // setIsStreaming callback
          'plugin', // platform
          undefined, // attachmentIds
          false, // skip_analysis
          true, // is_hidden_user_query
          actionType // actionType
        );
      }, [streaming]),
      isWeb: false, // Always false in plugin mode
      detectRtl: useCallback((text: string) => {
        // Simple RTL detection based on Arabic characters
        // Matches the old implementation pattern
        const arabicMatches = text.match(/[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g);
        const meaningfulText = text.replace(/[\s\n\r\t.,!?;:"'()[\]{}\-_=+*/\\|<>@#$%^&`~]/g, '');
        const totalChars = meaningfulText.length;
        
        if (totalChars === 0) return false;
        
        const arabicCount = arabicMatches ? arabicMatches.length : 0;
        // Use RTL if Arabic characters make up more than 25% of the meaningful content
        return (arabicCount / totalChars) > 0.25;
      }, [])
    } : undefined
  );

  // ========== Prototyper Facade ==========
  const prototyper = usePrototyperFacade(currentProjectId || '');

  // ========== Design Insights Facade ==========
  const designInsights = useDesignInsightsFacade();

  // ========== Headers Facade ==========
  const headers = useConversationHeadersFacade({
    isLoggedIn,
    onProjectCreationReady: useCallback((conversationId: string) => {
      if (onProjectCreationReady) {
        onProjectCreationReady(conversationId);
      } else {
        // Default behavior: navigate to project creation
        showToast({
          message: 'Ready to create project!',
          severity: 'success',
        });
      }
    }, [onProjectCreationReady, showToast]),
    onDrawingHeadersProcessed: useCallback((
      conversationHeaders: ConversationHeaders,
      currentId: string | null,
      newId?: string
    ) => {
      // Process drawing headers (sets canBeDrawn state)
      drawing.processDrawingHeaders(conversationHeaders);
      
      // In plugin mode, automatically trigger drawing creation when canBeDrawn is received
      // This matches the old implementation behavior where drawings are automatically created
      if (
        conversationHeaders.canBeDrawn &&
        platform === 'plugin' &&
        (currentId || newId)
      ) {
        const conversationIdToUse = currentId || newId!;
        const drawType = conversationHeaders.canBeDrawn;
        
        // Delay drawing creation slightly to ensure conversation is established
        // This matches the old implementation pattern:
        // - History loading needs 1 second delay (line 114-119 in old useConversationHistory.ts)
        // - Streaming headers can trigger immediately (line 78 in old useDrawingIntegration.ts)
        const delay = currentId ? 1000 : 0; // History loading needs delay, streaming doesn't
        setTimeout(() => {
          drawing.handleDrawingCreation(conversationIdToUse, drawType);
        }, delay);
      }
    }, [drawing, platform]),
    onAccountCreationRequired: useCallback(() => {
      if (onAccountCreationRequired) {
        onAccountCreationRequired();
      } else {
        // Default behavior: open auth dialog
        auth.openRegister?.();
      }
    }, [onAccountCreationRequired, auth]),
  });

  // ========== Header Config ==========
  const headerConfig = useHeaderConfig({
    onNavigateToProfile: navigation.toProfile,
    onNavigateToBilling: navigation.toBilling,
    onLoginClick: auth.openLogin,
    onRegisterClick: auth.openRegister,
    showToast,
    currentPath: '/',
  });

  // ========== Mode Management ==========
  // Sync local mode state with Redux (only if no initialMode from URL)
  useEffect(() => {
    if (!initialMode) {
      const mappedMode: ProjectMode = conversationMode === 'prototyper' ? 'prototyper' : 'uncover';
      if (mappedMode !== currentMode) {
        setCurrentMode(mappedMode);
      }
    }
  }, [conversationMode, currentMode, initialMode]);

  // Update conversation mode in Redux if initialMode is provided (e.g., from plugin redirect)
  useEffect(() => {
    if (initialMode && currentMode === initialMode) {
      const conversationModeValue: 'uncover' | 'prototyper' = initialMode === 'prototyper' ? 'prototyper' : 'uncover';
      dispatch(setMode(conversationModeValue as any));
    }
  }, [initialMode, currentMode, dispatch]);

  // Handle mode changes - update project mode and navigate to project details page
  const handleMenuModeChange = useCallback((newMode: ProjectMode) => {
    if (!currentProjectId) {
      console.warn('Cannot change mode: no current project ID');
      return;
    }

    // Update project mode in Redux store and API
    dispatch(updateProjectMode({ projectId: currentProjectId, mode: newMode }));
    
    // Navigate to project details page
    navigation.toProjectDetails?.(currentProjectId);
  }, [dispatch, currentProjectId, navigation]);

  // Handle mode changes - update both local state and Redux
  // For tmp conversations: only allow prototyper if accessed from onboarding
  const handleModeChange = useCallback((newMode: ProjectMode) => {
    // For tmp conversations (no project), validate prototyper access
    if (!currentProjectId && newMode === 'prototyper' && !isOnboardingPrototyper) {
      console.warn('Cannot switch to prototyper mode: not accessible from onboarding');
      showToast({
        message: 'Prototyper mode requires a project. Please create a project or access prototyper from onboarding.',
        severity: 'warning',
      });
      return;
    }

    setCurrentMode(newMode);
    // Map ProjectMode to ConversationStepMode
    const conversationModeValue: 'uncover' | 'prototyper' = newMode === 'prototyper' ? 'prototyper' : 'uncover';
    dispatch(setMode(conversationModeValue as any));
  }, [dispatch, currentProjectId, isOnboardingPrototyper, showToast]);


  // ========== Sidebar Config ==========
  const sidebar = useSidebarConfig({
    pageType: 'conversation',
    isLoggedIn,
    onResetConversation: useCallback(() => {
      // Reset conversation state and navigate to new conversation
      dispatch(resetConversation());
      navigation.toConversation?.();
    }, [dispatch, navigation]),
    onNavigateToLanding: useCallback(() => {
      navigation.toLanding?.();
    }, [navigation]),
    currentMode:conversationState.initiatedFromUncover ? 'uncover' : currentMode,
    // For tmp conversations (no project), use handleModeChange (updates Redux only)
    // For project conversations, use handleMenuModeChange (updates project + navigates)
    onModeChange: currentProjectId ? handleMenuModeChange : handleModeChange,
    onHomeClick: () => {
      navigation.toLanding?.();
    },
    hasProject: !!currentProjectId,
    isOnboardingPrototyper,
  });

  // ========== Effects ==========
  
  /**
   * Navigate to conversation when ID changes (after streaming creates new conversation)
   * Only navigate if:
   * 1. We have a conversation ID from Redux
   * 2. It's different from the URL conversation ID (new conversation created)
   * 3. We haven't navigated yet for this ID
   */
  useEffect(() => {
    if (
      conversationState.conversationId &&
      conversationState.conversationId !== urlConversationId &&
      !hasNavigatedRef.current
    ) {
      hasNavigatedRef.current = true;
      
      // Mark this as already loaded since we just created it (messages are already in Redux)
      lastLoadedConversationRef.current = conversationState.conversationId;
      hasFetchedDetailsRef.current.add(conversationState.conversationId);
      
      navigation.toConversation?.(conversationState.conversationId);
    }
  }, [conversationState.conversationId, urlConversationId, navigation]);

  /**
   * Clear onboarding prototyper flag when project is created
   * Once user has a project, they no longer need special onboarding access
   */
  useEffect(() => {
    if (currentProjectId && isOnboardingPrototyper) {
      conversation.setOnboardingPrototyper(false);
    }
  }, [currentProjectId, isOnboardingPrototyper, conversation]);

  /**
   * Fetch conversation details when URL conversation ID changes
   * This handles:
   * 1. Initial load of a conversation
   * 2. Switching between different conversations
   * 3. Clearing old messages and fetching new ones
   */
  useEffect(() => {
    const getDetailsFromState = (id: string) => {
      // Check if the current conversation state matches the requested ID
      // Either by conversationId (after streaming completes) OR by being the active conversation
      const isCurrentConversation = conversationState.conversationId === id || 
                                     (conversationState.isActive && !conversationState.conversationId);
      
      if (isCurrentConversation) {
        // Use the name from state, or init_message as fallback
        const title = conversationName || conversationState.init_message;
        
        if (title) {
          return {
            id: id,
            title: title,
            project_id: currentProjectId || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
        }
      }
      return null;
    };

    const fetchDetails = async () => {
      // Skip if no URL conversation ID
      if (!urlConversationId || !isValidConversationId(urlConversationId)) {
        return;
      }

      // Check if we're switching to a different conversation
      const isDifferentConversation = lastLoadedConversationRef.current !== urlConversationId;
      
      if (isDifferentConversation) {
        // Clear messages and reset state when switching conversations
        hasFetchedDetailsRef.current.clear();
        lastLoadedConversationRef.current = urlConversationId;
      }

      // Skip if already loading or already fetched this conversation
      if (isLoadingDetails || hasFetchedDetailsRef.current.has(urlConversationId)) {
        return;
      }

      hasFetchedDetailsRef.current.add(urlConversationId);
      
      // First, try to get details from current state
      const stateDetails = getDetailsFromState(urlConversationId);
      
      if (stateDetails) {
        // We have the conversation in state and it's the same conversation, use that
        
        if (stateDetails.title && !conversationName) {
          dispatch(setName(stateDetails.title));
        }
        
        if (stateDetails.project_id) {
          dispatch(setCurrentProjectId(stateDetails.project_id));
        }
        
        // Load history after details are set
        try {
          const loadedMessages = await streaming.loadHistory(urlConversationId, currentProjectId);
          
          // Process headers from last message after loading history
          headers.processHeadersFromLastMessage(loadedMessages, urlConversationId);
        } catch (historyError) {
          console.error('Failed to load history:', historyError);
          navigation.toLanding?.();
        }
      } else {
        // State doesn't have the conversation OR we switched conversations, fetch from API
        console.log('Fetching conversation details from API for ID:', urlConversationId);
        setIsLoadingDetails(true);
        
        try {
          const details = await streaming.fetchConversationDetails(urlConversationId);
          
          // Update Redux with conversation details
          if (details.title) {
            dispatch(setName(details.title));
          }
          
          if (details.project_id) {
            dispatch(setCurrentProjectId(details.project_id));
          }
          
          // Load history after details are fetched
          const loadedMessages = await streaming.loadHistory(urlConversationId, currentProjectId);
          
          // Process headers from last message after loading history
          headers.processHeadersFromLastMessage(loadedMessages, urlConversationId);
        } catch (error) {
          console.error('Failed to fetch conversation details:', error);
          // On error, still try to load history
          try {
            const loadedMessages = await streaming.loadHistory(urlConversationId, currentProjectId);
            if(loadedMessages.length === 0) {
              // If no messages, navigate to landing
              navigation.toLanding?.();
              return;
            }
            // Process headers from last message after loading history
            headers.processHeadersFromLastMessage(loadedMessages, urlConversationId);
          } catch (historyError) {
            console.error('Failed to load history:', historyError);
            // Navigate to landing on error
            navigation.toLanding?.();
          }
        } finally {
          setIsLoadingDetails(false);
        }
      }
    };

    fetchDetails();
  }, [
    urlConversationId, // Main trigger - when URL conversation ID changes
    isLoadingDetails,
    streaming,
    dispatch,
    currentProjectId,
    conversationState.conversationId,
    conversationName,
    conversationState.init_message,
    conversationState.isActive,
    navigation,
    headers,
    messages,
  ]);

  // ========== Actions ==========
  const actions = useMemo(() => ({
    submitMessage: (message: string, attachmentIds?: string[], onComplete?: (newConversationId?: string, headers?: ConversationHeaders) => void, modeOverride?: 'uncover' | 'prototyper') => {
      // Add message to Redux
      streaming.submitMessage(message, attachmentIds);
      
 
      
      // Start streaming if we have a conversation or session
      if (currentConversationId || conversationState.session_id) {
        streaming.createStreamingConversation(
          message,
          currentConversationId || undefined,
          onComplete,
          undefined, // setIsStreaming handled internally
          platform,
          attachmentIds,
          false, // skip_analysis: false 
          undefined, // isHiddenUserQuery
          undefined, // actionType
          conversationState.llmFamily, // Use llm_family from state
          modeOverride // Pass mode override
        );
      }
    },

    cancelStreaming: () => {
      streaming.cancelStreaming();
    },

    loadHistory: async (conversationId: string) => {
      return await streaming.loadHistory(conversationId, currentProjectId);
    },

    createDrawing: async (questionnaireType: string) => {
      if (currentConversationId) {
        await drawing.handleDrawingCreation(
          currentConversationId,
          questionnaireType as QuestionnaireType
        );
      }
    },

    createPrototype: async () => {
      if (currentConversationId && !prototyper.isCreatingPrototype) {
        await prototyper.handlePrototypeCreation(currentConversationId);
        // Refresh user profile to update credit balance after prototype creation
        if (onRefreshUserProfile) {
          onRefreshUserProfile().catch((error) => {
            console.error('Failed to refresh user profile after prototype creation:', error);
          });
        }
      }
    },

    cancelDrawing: () => {
      drawing.cancelDrawing();
    },

    navigateToConversation: (conversationId: string) => {
      navigation.toConversation?.(conversationId);
    },

    navigateToProjectDetails: (projectId: string) => {
      // When navigating back from conversation to project details,
      // use replace=true to restore the project details that was replaced by conversation
      navigation.toProjectDetails?.(projectId, true);
    },

    sendFeedback: async (type: 'like' | 'dislike', messageId: string, projectId?: string | null) => {
      try {
        const isLiked = type === 'like';
        await streaming.sendFeedback({
          messageID: messageId,
          isLiked,
          projectId: projectId || undefined,
        });
      } catch (error) {
        console.error('Failed to send feedback:', error);
        showToast({
          message: 'Failed to send feedback. Please try again.',
          severity: 'error',
        });
        throw error;
      }
    },

    handleFigJamAction: async (conversationId: string, conversationName: string, generateType: string | null, projectId?: string | null, hiddenMessage?: string | null) => {
      try {
        // Import the service dynamically to avoid circular dependencies
       
        await handleFigJamPluginAction(conversationId, conversationName, generateType, projectId, hiddenMessage);
      } catch (error) {
        console.error('Failed to handle FigJam action:', error);
        showToast({
          message: 'Failed to process request. Please try again.',
          severity: 'error',
        });
        throw error;
      }
    },

    cleanUserActions: async (actionType: string) => {
      try {
        await cleanUserActions(actionType);
      } catch (error) {
        console.error('Failed to clean user actions:', error);
        // Don't throw - this is a cleanup operation, not critical
      }
    },
  }), [
    streaming,
    drawing,
    prototyper,
    navigation,
    currentConversationId,
    currentProjectId,
    currentMode,
    conversationState.session_id,
    conversationState.llmFamily,
    showToast,
  ]);

  // ========== Return Composed Facade ==========
  return useMemo(
    () => ({
      state: {
        isLoggedIn,
        isAppLoading,
        currentProjectId,
        currentConversationId,
        messages,
        isStreaming: streaming.isStreaming, // Get from streaming facade
        isLoading: conversationState.isLoading,
        isLoadingDetails,
        isActive: conversationState.isActive,
        session_id: conversationState.session_id,
        conversationId: conversationState.conversationId || null,
        name: conversationState.name,
        init_message: conversationState.init_message,
        skip_analysis: conversationState.skip_analysis || false,
        initialAttachmentIds: conversationState.initialAttachmentIds,
        llmFamily: conversationState.llmFamily,
        planDetails,
        isOnboardingPrototyper,
        initiatedFromUncover: conversationState.initiatedFromUncover,
      },
      sidebar,
      mode: {
        currentMode,
        handleModeChange,
        handleMenuModeChange
      },
      header: headerConfig,
      navigation,
      conversation,
      streaming,
      drawing,
      prototyper,
      designInsights,
      headers,
      project,
      auth,
      actions,
    }),
    [
      isLoggedIn,
      isAppLoading,
      currentProjectId,
      currentConversationId,
      messages,
      isLoadingDetails,
      conversationState,
      sidebar,
      currentMode,
      handleModeChange,
      handleMenuModeChange,
      headerConfig,
      navigation,
      conversation,
      streaming,
      drawing,
      prototyper,
      designInsights,
      headers,
      project,
      auth,
      actions,
    ]
  );
}
